import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { CheckIcon, UserCogIcon } from 'lucide-react';
import { EncabezadoPagina } from '../components/layout/AppLayout';
import { EncabezadoPanel, EstadoVacio, Panel } from '../components/ui/Basicos';
import { ChipDias, PuntoSemaforo } from '../components/ui/Semaforo';
import { MarcaGoogle } from '../components/ui/Google';
import { useDatos } from '../contexts/DatosContext';
import { abogadoPorId } from '../data/abogados';
import { diasRestantes, fechaCorta } from '../utils/fechas';
import { ESTILO_SEMAFORO, nivelPorDias } from '../utils/semaforo';
import { plazosVigentes } from '../utils/consultas';
import type { NivelSemaforo } from '../types';
export function Vencimientos() {
  const {
    plazos,
    expedientes,
    sucursalActiva,
    rol,
    marcarPlazoCumplido,
    reasignarPlazo,
    abogados,
    eventoGoogleDe
  } = useDatos();
  const [soloMios, setSoloMios] = useState(false);
  const [nivelFiltro, setNivelFiltro] = useState<'todos' | NivelSemaforo>('todos');
  const [reasignando, setReasignando] = useState<string | null>(null);
  const puedeGestionar = rol !== 'Recepción/Archivo';
  const lista = useMemo(() => {
    return plazosVigentes(plazos).filter(p => {
      const exp = expedientes.find(e => e.id === p.expedienteId);
      if (!exp) return false;
      if (sucursalActiva !== 'todas' && exp.sucursalId !== sucursalActiva) return false;
      if (soloMios && p.responsableId !== 'ab-04') return false;
      if (nivelFiltro !== 'todos' && nivelPorDias(diasRestantes(p.fechaLimite)) !== nivelFiltro) return false;
      return true;
    });
  }, [plazos, expedientes, sucursalActiva, soloMios, nivelFiltro]);
  const cumplidos = plazos.filter(p => p.cumplido);
  const conteo = (nivel: NivelSemaforo) => plazosVigentes(plazos).filter(p => nivelPorDias(diasRestantes(p.fechaLimite)) === nivel).length;
  return <>
      <EncabezadoPagina titulo="Agenda de términos" descripcion="Todos los plazos vigentes del despacho, ordenados por urgencia." acciones={<label className="flex h-9 items-center gap-2 rounded border border-gris-200 bg-white px-3 text-sm text-gris-700" data-id="element-1688" data-mp="1garmtyvl695u">
            <input type="checkbox" checked={soloMios} onChange={e => setSoloMios(e.target.checked)} className="h-4 w-4 rounded border-gris-300 text-marino-700" data-id="element-1689" data-mp="1bcq1t8elnzrq" />
            Solo mis términos
          </label>} data-id="element-1687" data-mp="245v1sliqni69" />

      <div className="mb-4 flex flex-wrap gap-2" data-id="element-1690" data-mp="1z2u6lj3iy5kb">
        {(['todos', 'rojo', 'ambar', 'verde'] as const).map(n => {
        const activo = nivelFiltro === n;
        const etiqueta = n === 'todos' ? `Todos (${plazosVigentes(plazos).length})` : `${ESTILO_SEMAFORO[n].etiqueta} (${conteo(n)})`;
        return <button key={n} type="button" onClick={() => setNivelFiltro(n)} className={`inline-flex h-8 items-center gap-2 rounded border px-3 text-xs font-medium transition-colors duration-150 ${activo ? 'border-marino-800 bg-marino-800 text-white' : 'border-gris-200 bg-white text-gris-600 hover:border-gris-300'}`} data-id="element-1691" data-mp="dofzstetdc0m">
              {n !== 'todos' && <PuntoSemaforo nivel={n} data-id="element-1692" data-mp="3428yplzlgc35" />}
              {etiqueta}
            </button>;
      })}
      </div>

      <Panel data-id="element-1693" data-mp="3d0ubt1maqywj">
        <EncabezadoPanel titulo="Términos vigentes" descripcion="Rojo: 3 días o menos · Ámbar: hasta 10 días · Verde: en tiempo." data-id="element-1694" data-mp="3d0ktm07htw1e" />
        {lista.length === 0 ? <EstadoVacio mensaje="No hay términos vigentes con estos filtros" data-id="element-1695" data-mp="3mztm0ompn2ln" /> : <ul className="divide-y divide-gris-100" data-id="element-1696" data-mp="1nmfs4vq4y27w">
            {lista.map(p => {
          const exp = expedientes.find(e => e.id === p.expedienteId)!;
          const dias = diasRestantes(p.fechaLimite);
          const responsable = abogadoPorId(p.responsableId);
          return <li key={p.id} className="px-5 py-3.5" data-id="element-1697" data-mp="3e3t50lv5fzwc">
                  <div className="flex flex-wrap items-center gap-4" data-id="element-1698" data-mp="3e5dxm5xwl8rk">
                    <PuntoSemaforo nivel={nivelPorDias(dias)} data-id="element-1699" data-mp="3s1rjdr9gzwbu" />
                    <div className="min-w-0 flex-1" data-id="element-1700" data-mp="3o0xtiscgw7gb">
                      <p className="text-sm font-medium text-gris-800" data-id="element-1701" data-mp="3o28u6jam8z37">{p.concepto}</p>
                      <p className="mt-0.5 text-xs text-gris-500" data-id="element-1702" data-mp="30ceja79uxkll">
                        <Link to={`/expedientes/${exp.id}`} className="font-mono text-marino-700 hover:underline" data-id="element-1703" data-mp="2vdl66fyhfvly">
                          {exp.localizador}
                        </Link>
                        {' · '}
                        {exp.cliente}
                        {' · '}
                        <span className="text-gris-400" data-id="element-1704" data-mp="2cmais8ryu0yt">{p.tipo}</span>
                      </p>
                    </div>
                    <div className="w-40 shrink-0" data-id="element-1705" data-mp="22hu1rc2r2dtf">
                      <p className="flex items-center gap-1.5 text-xs text-gris-700" data-id="element-1706" data-mp="22h2f4qr4rqfa">
                        {responsable?.nombre}
                        {eventoGoogleDe('plazo', p.id) && <MarcaGoogle titulo="Recordatorio activo en su Google Calendar" data-id="element-1707" data-mp="1sm0ry0gf0yfc" />}
                      </p>
                      <p className="text-2xs text-gris-400" data-id="element-1708" data-mp="2l94u7xo4iynf">Cédula {responsable?.cedula}</p>
                    </div>
                    <div className="w-36 shrink-0 text-right" data-id="element-1709" data-mp="2q87yhfwa50uc">
                      <ChipDias fechaLimite={p.fechaLimite} data-id="element-1710" data-mp="2bapboj35wxpj" />
                      <p className="tabular mt-1 text-2xs text-gris-400" data-id="element-1711" data-mp="2ba5qmti7tt7a">
                        {fechaCorta(p.fechaLimite)}
                      </p>
                    </div>
                    {puedeGestionar && <div className="flex shrink-0 items-center gap-2" data-id="element-1712" data-mp="zxmwnsgpog7h">
                        <button type="button" onClick={() => setReasignando(reasignando === p.id ? null : p.id)} className="inline-flex h-8 items-center gap-1.5 rounded border border-gris-200 px-2.5 text-xs font-medium text-gris-700 transition-colors duration-150 hover:border-marino-300 hover:text-marino-700" data-id="element-1713" data-mp="14w6i7rre7pzb">
                          <UserCogIcon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-1714" data-mp="2ntjjmlmxf2qs" />
                          Reasignar
                        </button>
                        <button type="button" onClick={() => marcarPlazoCumplido(p.id)} className="inline-flex h-8 items-center gap-1.5 rounded bg-marino-800 px-2.5 text-xs font-semibold text-white transition-colors duration-150 hover:bg-marino-700" data-id="element-1715" data-mp="2jtso64mgfyfx">
                          <CheckIcon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-1716" data-mp="836sswotyu29" />
                          Cumplido
                        </button>
                      </div>}
                  </div>
                  {reasignando === p.id && <div className="mt-3 flex items-center gap-3 rounded border border-gris-200 bg-gris-50 px-3 py-2" data-id="element-1717" data-mp="pbhk4lnfzk7x">
                      <label className="text-xs font-medium text-gris-600" htmlFor={`re-${p.id}`} data-id="element-1718" data-mp="uau2at3dc346">
                        Nuevo responsable
                      </label>
                      <select id={`re-${p.id}`} value={p.responsableId} onChange={e => {
                reasignarPlazo(p.id, e.target.value);
                setReasignando(null);
              }} className="h-8 flex-1 rounded border border-gris-200 bg-white px-2 text-xs text-gris-700" data-id="element-1719" data-mp="1lwx9ro642td">
                        {abogados.filter(a => a.activo && a.rol !== 'Recepción/Archivo').map(a => <option key={a.id} value={a.id} data-id="element-1720" data-mp="2347dx5izbwzo">
                              {a.nombre} — {a.especialidades.join(', ')}
                            </option>)}
                      </select>
                    </div>}
                </li>;
        })}
          </ul>}
      </Panel>

      <Panel className="mt-4" data-id="element-1721" data-mp="2qs715vh3159i">
        <EncabezadoPanel titulo="Términos cumplidos" descripcion={`${cumplidos.length} registros en el histórico`} data-id="element-1722" data-mp="2qvmkjahsh0gq" />
        <ul className="divide-y divide-gris-100" data-id="element-1723" data-mp="30qwlduwgb1sx">
          {cumplidos.map(p => {
          const exp = expedientes.find(e => e.id === p.expedienteId);
          return <li key={p.id} className="flex items-center gap-4 px-5 py-2.5" data-id="element-1724" data-mp="16devw2chjuv9">
                <CheckIcon className="h-4 w-4 shrink-0 text-exito-500" aria-hidden="true" data-id="element-1725" data-mp="11etuhzlhz558" />
                <p className="min-w-0 flex-1 truncate text-xs text-gris-600" data-id="element-1726" data-mp="11h6l115bmntw">
                  {p.concepto} ·{' '}
                  <span className="font-mono text-marino-700" data-id="element-1727" data-mp="3i10h0si7e03f">{exp?.localizador}</span>
                </p>
                <p className="tabular shrink-0 text-2xs text-gris-400" data-id="element-1728" data-mp="33cxa9gwc6kdk">
                  {fechaCorta(p.fechaLimite)}
                </p>
              </li>;
        })}
        </ul>
      </Panel>
    </>;
}